import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Valid lookup categories.
 */
export const LOOKUP_CATEGORIES = [
  'lead_stages',
  'job_types',
  'document_categories',
  'service_types',
  'referral_sources',
  'payment_methods',
  'contact_categories',
  'contact_category',
  'salutations',
  'provinces',
  'communication_languages',
  'return_types',
  'practice_entities',
  'discount_definitions',
  'group_types',
  'relationship_types',
] as const;

export type LookupCategory = (typeof LOOKUP_CATEGORIES)[number];

/**
 * Human-readable labels for lookup categories.
 */
export const CATEGORY_LABELS: Record<LookupCategory, string> = {
  lead_stages: 'Lead Stages',
  job_types: 'Job Types',
  document_categories: 'Document Categories',
  service_types: 'Service Types',
  referral_sources: 'Referral Sources',
  payment_methods: 'Payment Methods',
  contact_categories: 'Contact Types',
  contact_category: 'Contact Categories',
  salutations: 'Salutations',
  provinces: 'Provinces',
  communication_languages: 'Communication Languages',
  return_types: 'Return Types',
  practice_entities: 'Practice Entities',
  discount_definitions: 'Discount Definitions',
  group_types: 'Group Types',
  relationship_types: 'Relationship Types',
};

/**
 * System lookup model from the API.
 */
export interface SystemLookup {
  id: string;
  tenantId: string;
  category: LookupCategory;
  code: string;
  label: string;
  description: string | null;
  displayOrder: number;
  isDefault: boolean;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLookupPayload {
  category: LookupCategory;
  code: string;
  label: string;
  description?: string | null;
  display_order?: number;
  is_default?: boolean;
  metadata?: Record<string, unknown>;
}

export interface UpdateLookupPayload {
  label?: string;
  description?: string | null;
  display_order?: number;
  is_default?: boolean;
  is_active?: boolean;
  metadata?: Record<string, unknown>;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const systemLookupsKeys = {
  all: ['system-lookups'] as const,
  byCategory: (category: LookupCategory) => [...systemLookupsKeys.all, category] as const,
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

function mapLookup(raw: Record<string, unknown>): SystemLookup {
  return {
    id: raw.id as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    category: (raw.category || '') as LookupCategory,
    code: (raw.code || '') as string,
    label: (raw.label || '') as string,
    description: (raw.description ?? null) as string | null,
    displayOrder: (raw.display_order ?? raw.displayOrder ?? 0) as number,
    isDefault: (raw.is_default ?? raw.isDefault ?? false) as boolean,
    isActive: (raw.is_active ?? raw.isActive ?? true) as boolean,
    metadata: (raw.metadata || {}) as Record<string, unknown>,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
  };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch all lookups for a specific category.
 */
export function useSystemLookups(category: LookupCategory, enabled = true) {
  return useQuery({
    queryKey: systemLookupsKeys.byCategory(category),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/system-lookups/${category}`,
      );
      const raw = response.data.data || [];
      return raw.map(mapLookup);
    },
    enabled,
  });
}

/**
 * Fetch all lookups grouped by category.
 */
export function useAllSystemLookups() {
  return useQuery({
    queryKey: systemLookupsKeys.all,
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, Record<string, unknown>[]>>>(
          '/system-lookups',
        );
      const grouped = response.data.data || {};
      const result: Record<LookupCategory, SystemLookup[]> = {} as Record<
        LookupCategory,
        SystemLookup[]
      >;
      for (const [cat, items] of Object.entries(grouped)) {
        result[cat as LookupCategory] = (items || []).map(mapLookup);
      }
      return result;
    },
  });
}

/**
 * Create a new system lookup.
 */
export function useCreateLookup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateLookupPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/system-lookups',
        payload,
      );
      return mapLookup(response.data.data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: systemLookupsKeys.byCategory(variables.category),
      });
      queryClient.invalidateQueries({ queryKey: systemLookupsKeys.all });
    },
  });
}

/**
 * Update an existing system lookup.
 */
export function useUpdateLookup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateLookupPayload }) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/system-lookups/item/${id}`,
        data,
      );
      return mapLookup(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemLookupsKeys.all });
      // Also invalidate all category queries
      for (const cat of LOOKUP_CATEGORIES) {
        queryClient.invalidateQueries({
          queryKey: systemLookupsKeys.byCategory(cat),
        });
      }
    },
  });
}

/**
 * Soft-delete (deactivate) a system lookup.
 */
export function useDeleteLookup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/system-lookups/item/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemLookupsKeys.all });
      for (const cat of LOOKUP_CATEGORIES) {
        queryClient.invalidateQueries({
          queryKey: systemLookupsKeys.byCategory(cat),
        });
      }
    },
  });
}

/**
 * Bulk reorder lookups within a category.
 */
export function useReorderLookups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      category,
      items,
    }: {
      category: LookupCategory;
      items: Array<{ id: string; display_order: number }>;
    }) => {
      await apiClient.patch(`/system-lookups/${category}/reorder`, { items });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemLookupsKeys.all });
      for (const cat of LOOKUP_CATEGORIES) {
        queryClient.invalidateQueries({
          queryKey: systemLookupsKeys.byCategory(cat),
        });
      }
    },
  });
}
